/**
 * R268s · Feb 2026 — Searchable dropdown (Popover + cmdk).
 *
 * A thin, reusable combobox that:
 *   • Renders a full-width trigger showing the currently selected label.
 *   • Opens a Popover with a fuzzy-search input and virtualised list.
 *   • Sorts entries alphabetically by `label` unless `preserveOrder`.
 *   • Optional `allowCreate` — surfaces a "Use …" row so the caller can
 *     accept a free-text value not present in the list (needed for
 *     new customer names).
 *
 * Props:
 *   value          — controlled string (matches an item's `value`)
 *   onChange(v,itm)— called with the picked value + full item
 *   options        — [{ value, label, hint?: string }]
 *   placeholder    — text on the trigger when nothing selected
 *   searchPlaceholder
 *   allowCreate    — bool, show "Use ‘query’" fallback row
 *   onCreate(text) — invoked when the free-text row is chosen
 *   testid         — data-testid prefix (`${testid}-trigger|input|item-<value>`)
 *   className      — extra trigger classes
 *   emptyLabel     — no-results message
 *   preserveOrder  — skip alphabetical sort (states already sorted by code, e.g.)
 *   renderLabel    — optional custom label renderer for the trigger
 */
import { useMemo, useState } from "react";
import { ChevronsUpDown, Check, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandItem, CommandGroup,
} from "@/components/ui/command";

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  allowCreate = false,
  onCreate,
  testid,
  className,
  emptyLabel = "No results.",
  preserveOrder = false,
  renderLabel,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const sorted = useMemo(() => {
    if (preserveOrder) return options;
    return [...options].sort((a, b) =>
      (a.label || "").localeCompare(b.label || "", undefined, {
        sensitivity: "base", numeric: true,
      }),
    );
  }, [options, preserveOrder]);

  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testid ? `${testid}-trigger` : undefined}
          className={cn(
            "h-11 w-full justify-between font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">
            {renderLabel && selected
              ? renderLabel(selected)
              : selected?.label || value || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={q}
            onValueChange={setQ}
            data-testid={testid ? `${testid}-input` : undefined}
          />
          <CommandList>
            <CommandEmpty>
              {allowCreate && q.trim()
                ? <span className="text-xs text-muted-foreground">Nothing matches — press ↵ or click below to use “{q.trim()}”.</span>
                : emptyLabel}
            </CommandEmpty>
            <CommandGroup>
              {sorted.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  onSelect={() => {
                    onChange?.(o.value, o);
                    setOpen(false);
                    setQ("");
                  }}
                  data-testid={testid ? `${testid}-item-${o.value}` : undefined}
                >
                  <Check className={cn("h-4 w-4",
                    value === o.value ? "opacity-100" : "opacity-0")} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{o.label}</div>
                    {o.hint && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {o.hint}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
              {allowCreate && q.trim() && !sorted.some((s) => s.label?.toLowerCase() === q.trim().toLowerCase()) && (
                <CommandItem
                  value={`__create__${q}`}
                  onSelect={() => {
                    onCreate?.(q.trim());
                    setOpen(false);
                    setQ("");
                  }}
                  data-testid={testid ? `${testid}-create` : undefined}
                >
                  <Plus className="h-4 w-4" />
                  <span>Use “<b>{q.trim()}</b>”</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
